import React from 'react';
import './pagination.css';

/**
 * Reusable Pagination Component
 * @param {Object} pagination - Pagination metadata object
 * @param {number} pagination.current_page - Current page number
 * @param {number} pagination.total_pages - Total number of pages
 * @param {number} pagination.next_page - Next page number (null if on last page)
 * @param {number} pagination.prev_page - Previous page number (null if on first page)
 * @param {number} pagination.total_count - Total number of items
 * @param {Function} onPageChange - Callback function when page changes
 * @param {boolean} loading - Whether a page request is currently pending
 */
export default function Pagination({ pagination, onPageChange, loading = false }) {
  if (!pagination || pagination.total_pages <= 1) {
    return null;
  }

  const { current_page, total_pages, next_page, prev_page } = pagination;

  const handlePageClick = (page) => {
    if (!loading && page !== current_page && page >= 1 && page <= total_pages) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    // Adjust if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page + ellipsis
    if (startPage > 1) {
      pages.push(
        <button key={1} className='pagination-btn' onClick={() => handlePageClick(1)} disabled={loading}>
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key='ellipsis-start' className='pagination-ellipsis'>
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${i === current_page ? 'active' : ''}`}
          onClick={() => handlePageClick(i)}
          disabled={loading}
          aria-current={i === current_page ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    // Ellipsis + last page
    if (endPage < total_pages) {
      if (endPage < total_pages - 1) {
        pages.push(
          <span key='ellipsis-end' className='pagination-ellipsis'>
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={total_pages}
          className='pagination-btn'
          onClick={() => handlePageClick(total_pages)}
          disabled={loading}
        >
          {total_pages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className='pagination-container' aria-busy={loading ? 'true' : 'false'}>
      <button
        className='pagination-btn pagination-arrow'
        onClick={() => handlePageClick(prev_page)}
        disabled={!prev_page || loading}
      >
        ‹
      </button>

      {renderPageNumbers()}

      <button
        className='pagination-btn pagination-arrow'
        onClick={() => handlePageClick(next_page)}
        disabled={!next_page || loading}
      >
        ›
      </button>
    </div>
  );
}
